import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './AveragingProgressPopup.css';

interface FixingEntry {
  Date: string;
  RealisedRate?: number;
  RealisedAt?: string;
  RealisedBy?: string;
}

interface AveragingProgressPopupProps {
  schedule: FixingEntry[];
  forwardPoints: number;
  finalPrice: number;
  position: { x: number; y: number };
  onClose: () => void;
}

const formatDate = (dateStr: string): string => {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-');
  return `${d}/${m}/${y}`;
};

const formatRate = (rate: number): string =>
  rate.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const AveragingProgressPopup: React.FC<AveragingProgressPopupProps> = ({
  schedule,
  forwardPoints,
  finalPrice,
  position,
  onClose,
}) => {
  const { t } = useTranslation();
  const popupRef = useRef<HTMLDivElement>(null);

  const sortedEntries = [...schedule]
    .filter(e => e.Date)
    .sort((a, b) => a.Date.localeCompare(b.Date));

  const total = sortedEntries.length;
  const realised = sortedEntries.filter(e => e.RealisedRate != null && e.RealisedRate > 0);
  const realisedCount = realised.length;
  const isComplete = finalPrice > 0;

  const currentAvg = realisedCount > 0
    ? realised.reduce((sum, e) => sum + (e.RealisedRate || 0), 0) / realisedCount
    : null;

  const projectedPrice = currentAvg !== null
    ? currentAvg + (forwardPoints || 0)
    : null;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    if (popupRef.current) {
      const menu = popupRef.current;
      const menuRect = menu.getBoundingClientRect();
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;

      if (position.x + menuRect.width > windowWidth) {
        menu.style.left = `${position.x - menuRect.width}px`;
      } else {
        menu.style.left = `${position.x}px`;
      }
      if (position.y + menuRect.height > windowHeight) {
        menu.style.top = `${position.y - menuRect.height}px`;
      } else {
        menu.style.top = `${position.y}px`;
      }
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [position, onClose]);

  return (
    <div
      ref={popupRef}
      className="averaging-progress-popup"
      style={{ position: 'fixed', left: position.x, top: position.y, zIndex: 9999 }}
    >
      <div className="averaging-progress-header">
        {isComplete
          ? t('grid.averagingProgress.completeHeader', { count: total })
          : t('grid.averagingProgress.header', { realised: realisedCount, total })}
      </div>

      <div className="averaging-progress-divider" />

      <table className="averaging-progress-table">
        <tbody>
          {sortedEntries.map(entry => {
            const hasRate = entry.RealisedRate != null && entry.RealisedRate > 0;
            return (
              <tr key={entry.Date} className={hasRate ? 'realised' : 'pending'}>
                <td className="avg-date">{formatDate(entry.Date)}</td>
                <td className="avg-rate">
                  {hasRate ? formatRate(entry.RealisedRate!) : '\u2014'}
                </td>
                <td className="avg-status">
                  {hasRate
                    ? t('grid.averagingProgress.realised')
                    : t('grid.averagingProgress.pending')}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {currentAvg !== null && (
        <>
          <div className="averaging-progress-divider" />
          <div className="averaging-progress-summary">
            <div className="avg-summary-row">
              <span className="avg-summary-label">
                {isComplete
                  ? t('grid.averagingProgress.finalAverage')
                  : t('grid.averagingProgress.currentAverage')}
              </span>
              <span className="avg-summary-value">{formatRate(currentAvg)}</span>
            </div>
            {projectedPrice !== null && (
              <div className="avg-summary-row">
                <span className="avg-summary-label">
                  {isComplete
                    ? t('grid.averagingProgress.finalPrice')
                    : t('grid.averagingProgress.projectedPrice')}
                </span>
                <span className="avg-summary-value">
                  {formatRate(projectedPrice)}
                  {!isComplete && (
                    <span className="avg-summary-hint">
                      {' '}({t('grid.averagingProgress.avgPlusFwdPts')})
                    </span>
                  )}
                </span>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AveragingProgressPopup;
